import {useState, useEffect, useRef, type MouseEvent} from 'react';

import {useIntl} from 'react-intl';

import {PencilIcon} from '@time-webkit/all/icons/pencil';
import type {EventParametersMap} from '@time-webkit/statist';

import {getTimestamp} from 'utils/datetime';
import DelayedAction from 'utils/delayed_action';

import Constants, {Locations} from 'utils/constants';

import {isRHSLocation} from './utils';

import {type PostMenuItem, type UsePostMenuItemsParams} from './types';
import {type usePostMenuData} from './use_post_menu_data';
import {type usePostMenuActions} from './post_context_menu/post_menu_actions';

export function usePostMenuEditItem(
    params: UsePostMenuItemsParams,
    postMenuData: ReturnType<typeof usePostMenuData>,
    actions: ReturnType<typeof usePostMenuActions>,
    logAction: (
        action: EventParametersMap['app.postAction']['action'],
        event: MouseEvent | KeyboardEvent
    ) => void,
): PostMenuItem | null {
    const {post, location} = params;
    const {formatMessage} = useIntl();

    const [isEditDisabled, setEditDisabled] = useState(false);
    const editDisableActionRef = useRef<DelayedAction>();

    if (!editDisableActionRef.current) {
        editDisableActionRef.current = new DelayedAction(() => setEditDisabled(true));
    }

    const canEdit = postMenuData.canEdit && !isEditDisabled;

    useEffect(() => {
        const disableCanEditPostByTime = () => {
            const {post} = params;

            const postEditTimeLimit = postMenuData.postEditTimeLimit || Constants.UNSET_POST_EDIT_TIME_LIMIT;

            if (canEdit && postMenuData.isLicensed && !postMenuData.currentUser?.isSystemAdmin) {
                if (postEditTimeLimit !== String(Constants.UNSET_POST_EDIT_TIME_LIMIT)) {
                    const milliseconds = 1000;
                    // eslint-disable-next-line no-mixed-operators
                    const timeLeft = post.create_at + Number(postEditTimeLimit) * milliseconds - getTimestamp();
                    if (timeLeft > 0) {
                        editDisableActionRef.current?.fireAfter(timeLeft + milliseconds);
                    }
                }
            }
        };

        if (postMenuData.disableEditAfterTimeLimit) {
            disableCanEditPostByTime();
        }

        return () => {
            editDisableActionRef.current?.cancel();
        };

        // did mount
        //eslint-disable-next-line
    }, []);

    if (!postMenuData.isReadOnlyChannel && canEdit) {
        return {
            attributes: {id: `edit_post_${post.id}`, 'data-qa': 'post_edit'},
            text: formatMessage({id: 'post_info.edit', defaultMessage: 'Edit'}),
            shortcut: 'E',
            icon: <PencilIcon width={18} height={18} />,
            action: (event) => {
                if (location === Locations.ACTIVITY_FEED) {
                    actions.focusPostToEdit(post.id, `${postMenuData.teamName}/activity`);
                } else {
                    const title =
                        post.root_id ? (
                            formatMessage({id: 'rhs_comment.comment', defaultMessage: 'Comment'})
                        ) : (
                            formatMessage({id: 'create_post.post', defaultMessage: 'Post'})
                        );

                    actions.setEditingPost(
                        post.id,
                        location === Locations.CENTER ? 'create_post' : 'create_comment',
                        title,
                        isRHSLocation(location),
                    );
                }

                logAction('edit', event);
            },
        };
    }

    return null;
}
